import {
  branchesService,
  cargoTypesService,
  carrierServicesService,
  carriersService,
  currenciesService,
  customersService,
  directInterlinesService,
  equipmentTypesService,
  linerTypesService,
  pricingTypesService,
  shipTypesService,
  tariffTypesService,
  transportTypesService,
} from '@/modules/entities';
import { formatDate } from '@/utils/date';

import type {
  Accessorial,
  AccessorialApi,
  Accessorials,
  AccessorialsApi,
  AvailableAccessorialCodeOption,
  AvailableAccessorialCodes,
  AvailableAccessorialCodesApi,
  Tariff,
  TariffApi,
  TariffPricing,
  TariffPricingApi,
} from '../models';

const CARRIER_CORPORATION_MAP: Record<string, string> = {
  N: 'No',
  Y: 'Yes',
};

const formatApiDate = (value: string | null | undefined): string => {
  if (!value) {
    return '-';
  }

  return formatDate(value, 'dd-MMM-yyyy') || '-';
};

export const normalizeTariff = async (data: TariffApi): Promise<Tariff> => {
  const [branch, carrier, service, equipment, cargoType, currency, linerType, pricingType, tariffType] =
    await Promise.all([
      branchesService.find(String(data.branchKey)),
      carriersService.find(String(data.carrierKey)),
      carrierServicesService.find(String(data.serviceKey)),
      equipmentTypesService.find(String(data.trailerKey)),
      cargoTypesService.find(data.cargoTypeCode),
      currenciesService.find(data.currencyCode),
      data.linerType ? linerTypesService.find(data.linerType) : null,
      pricingTypesService.find(data.pricingType),
      tariffTypesService.find(data.tariffType),
    ]);

  const formattedCarrierCorporation = data.carrierCorporation
    ? CARRIER_CORPORATION_MAP[data.carrierCorporation] || data.carrierCorporation
    : '-';

  return {
    branch,
    cargoType,
    carrier,
    carrierCorporation: formattedCarrierCorporation,
    createdBy: data.createdBy,
    createdOn: formatApiDate(data.createdOn),
    currency,
    customerKey: data.customerKey,
    effectiveDate: formatApiDate(data.effectiveDate),
    equipment,
    expirationDate: formatApiDate(data.expirationDate),
    isActive: data.isActive,
    linerType,
    modifiedBy: data.modifiedBy,
    modifiedOn: formatApiDate(data.modifiedOn),
    pricingType,
    service,
    tariffKey: data.tariffKey,
    tariffName: data.tariffName || '-',
    tariffStartDate: formatApiDate(data.tariffStartDate),
    tariffType,
  };
};

export const normalizeAvailableAccessorialCode = ([code, name]: [string, string]): AvailableAccessorialCodeOption => ({
  id: code,
  label: name,
  value: code,
});

export const normalizeAvailableAccessorialCodes = (codes: AvailableAccessorialCodesApi): AvailableAccessorialCodes => {
  const list = Object.entries(codes)
    .map(normalizeAvailableAccessorialCode)
    .sort((a, b) => a.label.localeCompare(b.label));

  return {
    list,
    map: list.reduce(
      (acc, option) => {
        acc[option.id] = option;
        return acc;
      },
      {} as AvailableAccessorialCodes['map'],
    ),
  };
};

export const filterAvailableAccessorialCodes = (
  codes: AvailableAccessorialCodes,
  search: string,
): AvailableAccessorialCodes => {
  const query = search.trim().toLowerCase();

  if (!query) {
    return codes;
  }

  const list = codes.list.filter(
    (option) => option.label.toLowerCase().includes(query) || option.id.toLowerCase().includes(query),
  );

  return {
    list,
    map: list.reduce(
      (acc, option) => {
        acc[option.id] = option;
        return acc;
      },
      {} as AvailableAccessorialCodes['map'],
    ),
  };
};

const normalizeTariffHeader = async (p: TariffPricingApi[number]): Promise<TariffPricing[number]> => {
  const [cargoType, customer, carrier] = await Promise.all([
    p.cargoType ? cargoTypesService.find(p.cargoType) : null,
    customersService.find(String(p.customerKey)),
    carriersService.find(String(p.carrierKey)),
  ]);

  return {
    ...p,
    cargoType,
    carrier,
    customer,
    id: String(p.pricingKey),
    pricingKey: String(p.pricingKey),
  };
};

export const normalizeTariffPricing = async (items: TariffPricingApi): Promise<TariffPricing> => {
  return await Promise.all(items.map(normalizeTariffHeader));
};

export const normalizeAccessorial = async (data: AccessorialApi): Promise<Accessorial> => {
  const [
    branch,
    carrier,
    service,
    customer,
    equipmentType,
    currency,
    pricingType,
    accessorialBasis,
    shipType,
    directInterline,
    transportType,
  ] = await Promise.all([
    branchesService.find(String(data.branchKey)),
    carriersService.find(String(data.carrierKey)),
    carrierServicesService.find(String(data.serviceKey)),
    customersService.find(String(data.customerKey)),
    equipmentTypesService.find(String(data.trailerKey)),
    data.currencyCode ? currenciesService.find(data.currencyCode) : null,
    data.pricingType ? pricingTypesService.find(data.pricingType) : null,
    data.accessorialBasis ? tariffTypesService.find(data.accessorialBasis) : null,
    data.shipType ? shipTypesService.find(data.shipType) : null,
    data.directInterline ? directInterlinesService.find(data.directInterline) : null,
    data.transportType ? transportTypesService.find(data.transportType) : null,
  ]);

  return {
    ...data,
    accessorialBasis,
    accessorialType: data.accessorialTypeDescription || data.accessorialType || '-',
    branch,
    carrier,
    createdByName: data.createdByName || '-',
    currency,
    customer,
    directInterline,
    equipmentType,
    modifiedByName: data.modifiedByName || '-',
    pricingType,
    service,
    shipType,
    transportType,
  };
};

export const normalizeAccessorials = async (data: AccessorialsApi): Promise<Accessorials> => ({
  items: await Promise.all((data.items ?? []).map(normalizeAccessorial)),
  totalCount: data.totalCount,
});
